import { Badge } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { StatusPill } from "@/features/marketplace/components/orders-list/StatusMenu";
import {
  usesVideoStatusChips,
  videoReviewBadgeVariant,
  videoStatusChips,
} from "@/features/marketplace/lib/line-status-display";
import type { MarketplaceFulfillmentViewer } from "@/features/marketplace/lib/fulfillment-badge-variant";
import type {
  OrderLineVideoReviewStatus,
  OrderScriptRow,
} from "@/features/marketplace/types/marketplace.types";

export function VideoStatusSummary({
  script,
  viewer = "partner",
}: {
  script: Pick<
    OrderScriptRow,
    "fulfillmentStatus" | "videos" | "videoStatusCounts"
  >;
  viewer?: MarketplaceFulfillmentViewer;
}) {
  const { t } = useTranslation();
  if (!usesVideoStatusChips(script)) {
    return <StatusPill status={script.fulfillmentStatus} viewer={viewer} />;
  }

  return (
    <span className="inline-flex max-w-full flex-wrap items-center gap-1">
      {videoStatusChips(script).map((chip) => (
        <Badge
          key={chip.status}
          variant={videoReviewBadgeVariant(chip.status)}
          dot
        >
          {videoStatusChipLabel(t, chip.status, chip.count)}
        </Badge>
      ))}
    </span>
  );
}

function videoStatusChipLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  status: OrderLineVideoReviewStatus,
  count: number,
): string {
  if (status === "REVISION_REQUESTED") {
    return t("marketplace.partner.videos.statusChips.needsRevision", {
      count,
      defaultValue_one: "Needs revision",
      defaultValue_other: "{{count}} need revision",
    });
  }
  return t("marketplace.partner.videos.statusChips.inReview", {
    count,
    defaultValue_one: "In review",
    defaultValue_other: "{{count}} in review",
  });
}
